import { AuthCredentials, AuthRepository, AuthResponse } from '../../domain/repositories/AuthRepository';
import { InvalidCredentialsError, NetworkError, UnknownError } from '../../domain/errors/AuthErrors';

import { AxiosError } from 'axios';
import { TokenService } from '../../application/services/TokenService';
import axios from '@/common/infrastructure/axios';

const { VITE_API_BASE_URL, VITE_API_AUTH_URL } = import.meta.env;
const LOGIN_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/login`;
const LOGOUT_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/logout`;

export class ApiAuthRepository implements AuthRepository {
  constructor(private readonly tokenService: TokenService) {}

  async login(credentials: AuthCredentials): Promise<AuthResponse> {
    try {
      const { userName, password } = credentials;
      const response = await axios.post(LOGIN_URL, { UserName: userName, password });
      if (response.data.isError || response.data.Authorization == '') {
        throw new Error(response.data.Message);
      }
      const token = response.data.Authorization;

      this.tokenService.setToken(token);
      this.setAuthHeader(token);

      // Save the complete login response data to session storage
      const loginData = {
        ...response.data,
        userName,
        loginTimestamp: new Date().toISOString(),
      };
      localStorage.setItem('loginData', JSON.stringify(loginData));

      return {
        token,
        user: {
          userName,
        },
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new InvalidCredentialsError();
        } else if (error.code === 'ECONNABORTED' || !error.response) {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async logout(): Promise<void> {
    // Best-effort: notify the server that this session is logging out so it
    // can stamp LogoutOn in AppGlobalUserLog. Must run BEFORE we clear the
    // auth header/token, otherwise the request would be unauthenticated.
    try {
      const globalUserId = this.getGlobalUserIdFromStorage();
      if (globalUserId > 0) {
        await axios.post(LOGOUT_URL, { GlobalUserID: globalUserId });
      }
    } catch (error) {
      console.warn('Server logout failed; continuing with local cleanup.', error);
    }

    // Original local cleanup — always runs regardless of the server result.
    this.tokenService.removeToken();
    this.clearAuthHeader();
    // Clear login data from session storage
    localStorage.removeItem('loginData');
  }

  private getGlobalUserIdFromStorage(): number {
    try {
      const raw = localStorage.getItem('loginData');
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      return Number(parsed?.UserID ?? parsed?.userID ?? parsed?.GlobalUserID ?? 0) || 0;
    } catch {
      return 0;
    }
  }

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    activationCode: string,
    username: string,
    phone: string,
  ): Promise<void> {
    try {
      const REGISTER_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/create-user`;
      const response = await axios.post(REGISTER_URL, {
        ActivationCode: activationCode,
        Username: username,
        Password: password,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: phone,
      });

      if (response.data?.isError || response.data?.isWarning) {
        throw new Error(response.data?.Message || 'Registration failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          const message = error.response.data?.Message || error.response.data?.message || 'Registration failed.';
          throw new Error(typeof message === 'string' ? message : 'Registration failed');
        } else if (!error.response || error.code === 'ECONNABORTED') {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async signup(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    username: string,
    phone: string,
  ): Promise<void> {
    try {
      const SIGNUP_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/signup-user`;
      const response = await axios.post(SIGNUP_URL, {
        Username: username,
        Password: password,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: phone,
      });

      if (response.data?.isError || response.data?.isWarning) {
        throw new Error(response.data?.Message || 'Signup failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          const message = error.response.data?.Message || error.response.data?.message || 'Signup failed.';
          throw new Error(typeof message === 'string' ? message : 'Signup failed');
        } else if (!error.response || error.code === 'ECONNABORTED') {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async resetPassword(username: string): Promise<void> {
    try {
      const VERIFICATION_CODE_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/verification-code`;
      const response = await axios.post(VERIFICATION_CODE_URL, { Username: username });
      
      if (response.data?.isError) {
        throw new Error(response.data.Message || 'Failed to send verification code');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          const message = error.response.data?.Message || error.response.data?.message || 'The username does not exist.';
          throw new Error(typeof message === 'string' ? message : 'Invalid username');
        } else if (!error.response || error.code === 'ECONNABORTED') {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async forgotUsername(email: string): Promise<void> {
    try {
      const FORGOT_USERNAME_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/forgot-username`;
      const response = await axios.post(FORGOT_USERNAME_URL, { Email: email });
      
      // Handle error responses from the API
      if (response.data?.isError) {
        throw new Error(response.data.Message || 'Failed to send username recovery email');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          // BadRequest - throw the server's error message
          const message = error.response.data?.message || error.response.data || 'Username matching to the provided email address cannot be found.';
          throw new Error(typeof message === 'string' ? message : 'Invalid email address');
        } else if (!error.response || error.code === 'ECONNABORTED') {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async verifyCode(username: string, verificationCode: string): Promise<number> {
    try {
      const VERIFY_CODE_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/verify-code`;
      const response = await axios.post(VERIFY_CODE_URL, { 
        Username: username, 
        VerificationCode: verificationCode 
      });
      
      if (response.data?.isError || !response.data?.isValid) {
        throw new Error(response.data?.Message || 'Invalid verification code');
      }

      return response.data?.UserID || 0;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          const message = error.response.data?.Message || error.response.data?.message || 'Invalid verification code.';
          throw new Error(typeof message === 'string' ? message : 'Invalid verification code');
        } else if (!error.response || error.code === 'ECONNABORTED') {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async updatePassword(globalUserId: number, password: string, verificationToken?: string): Promise<void> {
    try {
      const UPDATE_PASSWORD_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/update-password`;
      const requestBody: any = { 
        GlobalUserID: globalUserId, 
        Password: password 
      };
      if (verificationToken) {
        requestBody.VerificationToken = verificationToken;
      }
      const response = await axios.post(UPDATE_PASSWORD_URL, requestBody);
      
      if (response.data?.isError) {
        throw new Error(response.data?.Message || 'Failed to update password');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          const message = error.response.data?.Message || error.response.data?.message || 'Failed to update password.';
          throw new Error(typeof message === 'string' ? message : 'Failed to update password');
        } else if (error.response?.status === 401) {
          throw new Error('Unauthorized. Please try again.');
        } else if (!error.response || error.code === 'ECONNABORTED') {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  async updateUser(
    globalUserId: number,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ): Promise<void> {
    try {
      const UPDATE_USER_URL = `${VITE_API_BASE_URL}${VITE_API_AUTH_URL}/update-user`;
      const response = await axios.post(UPDATE_USER_URL, {
        GlobalUserID: globalUserId,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: phone,
      });

      if (response.data?.isError) {
        throw new Error(response.data?.Message || 'Failed to update user');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          const message = error.response.data?.Message || error.response.data?.message || 'Failed to update user.';
          throw new Error(typeof message === 'string' ? message : 'Failed to update user');
        } else if (error.response?.status === 401) {
          throw new Error('Unauthorized. Please try again.');
        } else if (!error.response || error.code === 'ECONNABORTED') {
          throw new NetworkError();
        }
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  }

  verifyToken(token: string): boolean {
    return this.tokenService.verifyToken(token);
  }

  private setAuthHeader(token: string): void {
    axios.defaults.headers.common.Authorization = `rca ${token}`;
  }

  private clearAuthHeader(): void {
    delete axios.defaults.headers.common.Authorization;
  }
}
